import React, { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { SectionTitle } from "./_components/SectionTitle";
import StarRating from "../../components/StarRating";
import {
  Airplay,
  Award,
  BookOpenText,
  CalendarClock,
  CheckCircle,
  CircleDot,
  Eye,
  FileText,
  Globe,
  LockKeyhole,
  Star,
} from "lucide-react";
import MainButton from "../../components/MainButton";
import { Avatar, Spin, Tooltip } from "antd";
import { title } from "motion/react-client";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import { cn } from "../../lib/utils";
import { motion } from "framer-motion";
import { DateFormate } from "./../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

export default function MainCourseDetails() {
  const location = useLocation();
  const course = location.state?.course;

  const { token, user } = useAuth();

  const navigate = useNavigate();
  const { language } = useLanguage();

  const SubInfo = [
    "النطق الصحيح (المخرج): تعلم كيفية نطق كل حرف عربي من نقطة النطق الصحيحة.",
    "قواعد التجويد المبسطة: فهم وتطبيق القواعد الأساسية للتجويد بما في ذلك الإدغام والإخفاء والقَلقَلة والمزيد.",
    "تحسين التلاوة: تطوير تلاوة سلسة وواثقة بالسريعة والإيقاع والتنغيم الصحيحة.",
    "أخطاء التلاوة الشائعة: تعرف وإصلاح الأخطاء الشائعة التي تحدث أثناء قراءة القرآن.",
    "دعم الحفظ (الذاكرة): نصائح وإرشادات لبدء أو تحسين رحلة حفظ الخاصة بك.",
    "التطبيق العملي: التلاوة أثناء الجلسات الحية وتلقي تعليقات من معلمك.",
    "الاتصال الروحي: قم ببناء علاقة أعمق وأكثر جدوى مع القرآن أثناء تحسين تلاوتك.",
    "الثقة والاتساق: اكتسب الثقة في التلاوة في الصلاة والبيئات العامة من خلال الممارسة المنتظمة.",
  ];

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/reviews/course/${course.id}`
      );
      setReviews(data.data);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  return (
    <div className="space-y-20 max-w-7xl mx-auto px-4 mt-14">
      <div className="space-y-10">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">الصفحة الرئيسية</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbEllipsis />
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/courses">الدورات</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbPage className="font-semibold">
                {course.title[language] || course.title.en || course.title.ar}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Course Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 min-h-[300px]">
          <div className="space-y-10 h-full flex flex-col justify-between">
            <SectionTitle
              title={
                course.title[language] || course.title.en || course.title.ar
              }
              className={"text-start"}
            />

            <div className="space-y-2">
              <p className="font-bold">وصف الدورة:</p>
              <div
                className="text-muted-foreground break-all"
                dangerouslySetInnerHTML={{
                  __html:
                    course.description[language] ||
                    course.description.en ||
                    course.description.ar,
                }}
              />
            </div>

            <div className="flex items-center gap-2">
              <p className="font-semibold">5 / {Number(course.rate)}</p>
              <StarRating rating={course.rate} />
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <span className="w-14">تابع ل</span>{" "}
                <span className="font-bold">
                  :{" "}
                  {course.category
                    ? course.category.title[language]
                    : course.sub_category
                    ? course.sub_category.title[language]
                    : "لا يوجد"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-muted-foreground" />
                <span className="w-14">التاريخ</span>{" "}
                <span className="font-bold">
                  : {DateFormate(course.created_at)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <CircleDot className="w-5 h-5 text-muted-foreground" />
                <span className="w-14">الحالة</span>{" "}
                <span className="font-bold">
                  : {course.type === 1 ? "مباشر" : "مسجل"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-muted-foreground" />
                <span className="w-14">الشهادة</span>
                <span className="font-bold">: عند الانتهاء من الدورة</span>
              </div>
            </div>

            <MainButton
              title={"تسجيل الدورة"}
              Icon={Airplay}
              size={"lg"}
              className={"w-full"}
              onClick={() =>
                navigate(
                  !token ? "/login" : user.role === "student" && "/all-courses"
                )
              }
            />
          </div>

          <img
            src="/metion.jpg"
            alt="course"
            className="object-cover rounded-xl h-full w-full shadow-md"
          />
        </div>
      </div>

      {/* Sub Info */}
      {/* <div className="space-y-6">
        <div className="space-y-4">
          <h2 className="font-bold text-2xl">ما ستتعلمه:</h2>
          <ul className="space-y-2 mt-2">
            {SubInfo.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.2 }}
              >
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                  <span>{item}</span>
                </li>
              </motion.div>
            ))}
          </ul>
        </div>

        <div className="space-y-4">
          <h2 className="font-bold text-2xl">لمن هذه الدورة:</h2>
          <ul className="list-disc pr-5 mt-2 space-y-2">
            <li>المسلمون غير الناطقين باللغة العربية.</li>
            <li>المبتدئين في تلاوة القرآن.</li>
            <li>الطلاب الذين يرغبون في تحسين التجويد.</li>
            <li>
              اليافعون والأطفال الذين يبحثون عن التعلم المنظم الموجه من قبل
              المعلم.
            </li>
          </ul>
        </div>
      </div> */}

      <div
        className="break-all text-lg"
        dangerouslySetInnerHTML={{
          __html:
            course.description[language] ||
            course.description.en ||
            course.description.ar,
        }}
      />

      {/* Sub Lessons */}
      {/* <div className="space-y-6">
        <h2 className="font-bold text-2xl">محتوي الدورة:</h2>

        <div className="border p-4 rounded-xl space-y-4">
          {[1, 2, 3, 4].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <LessonCard
                title={"كيفية النطق السليم للحروف العربية"}
                files={12}
                homeworks={5}
                isLock={index % 2 !== 0 && true}
              />
            </motion.div>
          ))}
          <div className="text-center pt-5">
            <MainButton title={"تحميل المزيد من محتوي الدورة"} size={"lg"} />
          </div>
        </div>
      </div> */}

      {/* Review */}
      {reviews.length !== 0 && (
        <div className="space-y-6">
          <h2 className="font-bold text-2xl">
            بعض آراء الطلاب :{" "}
            <span className="text-sm text-muted-foreground">
              ({reviews.length} تقيمات)
            </span>
          </h2>
          {loadingReviews ? (
            <div className="flex items-center justify-center w-full h-36">
              <Spin size="large" />
            </div>
          ) : (
            reviews.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <StudentComments
                  avatar={item?.image}
                  name={item?.name}
                  comment={item?.message}
                  timestamp={DateFormate(item?.created_at)}
                  rating={item?.rate ? Number(item?.rate) : undefined}
                />
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const LessonCard = ({ title, files, homeworks, isLock }) => {
  return (
    <div className="border p-4 rounded-xl bg-muted flex items-center">
      <p className="flex-1 font-bold text text-lg">{title}</p>

      <div className="flex items-center gap-2 flex-1">
        <BookOpenText className="w-5 h-5 text-muted-foreground" />
        <span className="">{files} ملف</span>
      </div>

      <div className="flex items-center gap-2 flex-1">
        <FileText className="w-5 h-5 text-muted-foreground" />
        <span className="">{homeworks} واجبات</span>
      </div>

      {isLock ? (
        <Tooltip title="قم بلتسجيل بلدورة أولا" className="cursor-pointer">
          <LockKeyhole />
        </Tooltip>
      ) : (
        <Tooltip title="مشاهدة مجانية" className="cursor-pointer">
          <Eye />
        </Tooltip>
      )}
    </div>
  );
};

const StudentComments = ({ avatar, name, comment, timestamp, rating }) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar
        className={cn(
          "min-w-12 min-h-12 rounded-full flex items-center justify-center text-xs font-medium",
          "bg-blue-100 text-blue-600",
          "dark:bg-blue-900 dark:text-blue-300"
        )}
        src={`${UrlImage}${avatar}`}
      >
        {name.charAt(0)}
      </Avatar>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center gap-4">
          <p className="text-xs font-bold">{name}</p>
          <div className="flex items-center gap-1 text-sm">
            <Star className={`w-4 h-4 text-yellow-400 fill-yellow-400`} />
            {rating}
          </div>
        </div>
        <div className="bg-secondary w-full py-3 px-6 rounded-xl">
          <p className="font-medium text-base md:text-lg">{comment}</p>
        </div>
        <p className="text-xs font-medium text-muted-foreground">{timestamp}</p>
      </div>
    </div>
  );
};
